import { Radio } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Token } from '@stripe/stripe-js';
import { Body, Button, Heading } from 'copilot-design-system';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Stripe from 'stripe';

import { PortalConfigContext } from 'src/context/portalConfigContext';
import { usePaymentProcessingFee } from 'src/legacy/components/billing/new/usePaymentProcessingFee';
import { BankVerifyActionProps } from 'src/legacy/components/PaymentMethodForm/BankVerifyButton';
import { AddCreditCardForm } from 'src/routes/billing/components/AddCreditCardForm';
import { PaymentMethodOption } from 'src/routes/billing/components/PaymentMethodOption';
import {
  PaymentMethodType,
  PaymentPreferences,
} from 'src/services/api/invoicesApi';
import { StripePaymentMethod } from 'src/types/payment';
import { getPaymentMethodDetailsFromSource } from 'src/utils/BillingUtils';

type HandleSaveValues = {
  sourceId?: string;
  isPrimary: boolean;
  name?: string;
  postalCode?: string;
  fields?: {
    token: Token;
  };
};

export enum FeePayer {
  Internal = 'us',
  Client = 'client',
}

export const isValidFeePayer = (value: string): value is FeePayer => {
  return value === FeePayer.Internal || value === FeePayer.Client;
};

type SavedPaymentMethodModalProps = {
  defaultSourceId: string;
  showActions?: boolean;
  onAddClicked: (values: HandleSaveValues) => Promise<void>;
  isSourcesLoading: boolean;
  handleDelete: (sourceId: string) => void;
  onUpdatePaymentPreferences: (preferences: PaymentPreferences) => void;
  onUpdatingPrimarySource: (source: StripePaymentMethod) => void;
  paymentPreferences: PaymentPreferences;
  recipientId: string;
  totalAmount: number;
  sources: StripePaymentMethod[];
  showAddCreditCardForm: boolean;
  setShowAddCreditCardForm: (show: boolean) => void;
  bankAccounts?: Record<string, Stripe.BankAccount>;
  selectedSourceId: string | undefined | null;
  onSetSelectedSourceId: (sourceId: string | null | undefined) => void;
} & Partial<BankVerifyActionProps>;

/**
 *
 * This component is used to manage payment methods for auto charge invoices and subscriptions
 * It allows users to:
 * - View and select from their saved payment methods.
 * - Set a default payment method.
 * - Add a new payment method.
 * - Choose who will pay the associated transaction fee.
 *
 * @param {string} props.recipientId - The ID of the recipient whose payment methods are being managed.
 */
export const SavedPaymentMethodModal = ({
  defaultSourceId,
  handleDelete,
  isSourcesLoading,
  onAddClicked,
  onUpdatePaymentPreferences,
  paymentPreferences,
  totalAmount,
  sources,
  showAddCreditCardForm,
  setShowAddCreditCardForm,
  bankAccounts,
  selectedSourceId,
  onSetSelectedSourceId,
  onUpdatingPrimarySource,
  onVerify,
}: SavedPaymentMethodModalProps) => {
  const { t } = useTranslation();
  const portalConfig = useContext(PortalConfigContext);
  const portalName = portalConfig.name;

  const { card, bankAccount } = useMemo(() => {
    return getPaymentMethodDetailsFromSource(
      sources.find((source) => source.id === selectedSourceId),
    );
  }, [selectedSourceId, sources]);

  const isTransactionFeePaidByClient = useMemo(
    () =>
      !!(
        card &&
        !paymentPreferences.absorbTransactionFee.includes(
          PaymentMethodType.CreditCard,
        )
      ) ||
      !!(
        bankAccount &&
        !paymentPreferences.absorbTransactionFee.includes(
          PaymentMethodType.BankAccount,
        )
      ),
    [card, bankAccount, paymentPreferences],
  );

  const { creditCardFeeDetails, bankAccountFeeDetails } =
    usePaymentProcessingFee({
      amountDue: totalAmount * 100,
      source: selectedSourceId,
      preferences: paymentPreferences,
    });

  const feeLabel = useMemo(() => {
    if (!selectedSourceId) return;

    if (card) {
      return `${creditCardFeeDetails.feeLabel} fee`;
    }

    if (bankAccount) {
      return `${bankAccountFeeDetails.feeLabel} fee`;
    }

    return 'fee';
  }, [
    selectedSourceId,
    card,
    bankAccount,
    creditCardFeeDetails.feeLabel,
    bankAccountFeeDetails.feeLabel,
  ]);

  const handleShowAddCreditCardForm = () => {
    setShowAddCreditCardForm(true);
  };

  const handleDismissAddCreditCardForm = () => {
    setShowAddCreditCardForm(false);
  };

  const handleSourceChange = (newSourceId: string) => {
    onSetSelectedSourceId(newSourceId);
  };

  const handleFeesPreferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (isValidFeePayer(event.target.value)) {
      onUpdatePaymentPreferences({
        ...paymentPreferences,
        absorbTransactionFee:
          event.target.value === FeePayer.Client
            ? []
            : [PaymentMethodType.CreditCard, PaymentMethodType.BankAccount],
      });
    }
  };

  return (
    <div>
      {isSourcesLoading &&
        Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} width="100%" height={48} className="rouned-4" />
        ))}
      {!isSourcesLoading &&
        sources.map((source) => (
          <PaymentMethodOption
            key={source.id}
            source={source}
            isDefault={source.id === defaultSourceId}
            onSelect={handleSourceChange}
            isSelected={selectedSourceId === source.id}
            onMakeDefault={onUpdatingPrimarySource}
            onDelete={handleDelete}
            bankAccount={bankAccounts && bankAccounts[source.id]}
            onVerify={onVerify}
          />
        ))}
      <div className="mt-3 mb-5">
        {showAddCreditCardForm ? (
          <AddCreditCardForm
            onDismiss={handleDismissAddCreditCardForm}
            onSave={onAddClicked}
          />
        ) : (
          <Button
            onClick={handleShowAddCreditCardForm}
            label="Add new card"
            prefixIcon="Plus"
            className="[&>p]text-[12px] bg-transparent text-black-heading border-0 hover:bg-transparent [&>svg]:pr-1 p-0"
          />
        )}
      </div>
      {(card || bankAccount) && (
        <div className="flex justify-between border border-solid border-non-hover-border rounded-4 p-3">
          <div>
            <Heading className="text-[14px] text-dark-black">
              Decide who pays the fee
            </Heading>
            {feeLabel ? (
              <Body className="text-[12px] text-gray-small">
                {isTransactionFeePaidByClient
                  ? t(($) => $.page_billing.fee_payer.cu, {
                      feeLabel: feeLabel,
                    })
                  : t(($) => $.page_billing.fee_payer.iu, {
                      portalName: portalName,
                    })}
              </Body>
            ) : null}
          </div>
          <div>
            <label className="flex gap-2 items-center justify-end mb-2">
              <Body className="text-[14px] text-dark-black">Us</Body>
              <Radio
                size="small"
                className="p-0"
                value={FeePayer.Internal}
                checked={!isTransactionFeePaidByClient}
                color="primary"
                onChange={handleFeesPreferenceChange}
              />
            </label>
            <label className="flex gap-2 items-center justify-end">
              <Body className="text-[14px] text-dark-black">
                {t(($) => $.client, { count: 1 })}
              </Body>
              <Radio
                size="small"
                className="p-0"
                value={FeePayer.Client}
                checked={isTransactionFeePaidByClient}
                onChange={handleFeesPreferenceChange}
                color="primary"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
